import { Box, CircularProgress, Container } from '@mui/material';
import type { FC, PropsWithChildren } from 'react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { EMBED_URL_CACHE_MS } from '~/constants';
import { useEmbedUrl } from '~/hooks/useEmbedUrl';
import { useQuickSightEmbed } from '~/hooks/useQuickSightEmbed';

interface QuickSightEmbedContextProps {
  showDashboard: (dashboardId: string) => void;
  hideDashboard: () => void;
  activeDashboardId: string | null;
  isLoading: boolean;
  error: Error | null;
  clearError: () => void;
}

const QuickSightEmbedContext = createContext<QuickSightEmbedContextProps | undefined>(undefined);

/**
 * Persistent QuickSight embed provider that lives above the router.
 *
 * The SDK creates internal resources (control frame, timers, listeners) with no
 * public cleanup API. By keeping the embed container always mounted, we avoid
 * the unmount/remount cycle that causes "Creating the frame timed out" crashes.
 *
 * The container is shown/hidden via CSS to preserve the iframe across
 * route navigations. Dashboard switching uses navigateTo() within the existing
 * iframe.
 */
export const QuickSightEmbedProvider: FC<PropsWithChildren> = ({ children }) => {
  const [activeDashboardId, setActiveDashboardId] = useState<string | null>(null);
  const [providerError, setProviderError] = useState<Error | null>(null);

  const {
    sessionBaseUrl,
    getEmbedUrl,
    isLoading: isUrlLoading,
    error: urlError,
    onEmbedSuccess,
    invalidateSession,
  } = useEmbedUrl(activeDashboardId ?? undefined);

  const {
    containerRef,
    embed,
    navigateTo,
    reset,
    isEmbedded,
    isLoading: isEmbedLoading,
    error: embedError,
  } = useQuickSightEmbed();

  const isLoading = isUrlLoading || isEmbedLoading;

  const isInitializingRef = useRef(false);
  const currentDashboardIdRef = useRef<string | null>(null);
  const sessionExpiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasRetriedRef = useRef(false);

  const clearError = useCallback(() => setProviderError(null), []);

  const showDashboard = useCallback((dashboardId: string) => {
    setProviderError(null);
    setActiveDashboardId(dashboardId);
  }, []);

  const hideDashboard = useCallback(() => {
    setActiveDashboardId(null);
  }, []);

  // Handle URL fetch errors
  useEffect(() => {
    if (urlError) {
      setProviderError(urlError instanceof Error ? urlError : new Error('Failed to fetch embed URL'));
      invalidateSession();
      setActiveDashboardId(null);
    }
  }, [urlError, invalidateSession]);

  // Handle post-embed SDK errors (e.g. ERROR_OCCURRED event after successful embed)
  useEffect(() => {
    if (embedError && isEmbedded) {
      setProviderError(embedError);
      invalidateSession();
      setActiveDashboardId(null);
    }
  }, [embedError, isEmbedded, invalidateSession]);

  // Clean up SDK state and session expiry timer on unmount (logout)
  useEffect(() => {
    return () => {
      if (sessionExpiryTimeoutRef.current) {
        clearTimeout(sessionExpiryTimeoutRef.current);
      }
      reset();
    };
  }, [reset]);

  // Main embed lifecycle
  useEffect(() => {
    if (!activeDashboardId || !sessionBaseUrl || isInitializingRef.current) {
      return;
    }

    const initialize = async () => {
      try {
        if (!isEmbedded) {
          // Initial embed: build URL for requested dashboard and embed directly
          isInitializingRef.current = true;

          const embedUrl = getEmbedUrl(activeDashboardId);

          if (!embedUrl) {
            throw new Error('Failed to build embed URL');
          }

          await embed(embedUrl);
          onEmbedSuccess(embedUrl);

          // Proactively expire the session before the QS iframe becomes stale
          sessionExpiryTimeoutRef.current = setTimeout(() => {
            invalidateSession();
            reset();
            setActiveDashboardId(null);
          }, EMBED_URL_CACHE_MS);

          currentDashboardIdRef.current = activeDashboardId;
          isInitializingRef.current = false;
          hasRetriedRef.current = false;
        } else if (currentDashboardIdRef.current !== activeDashboardId) {
          // Already embedded: use SDK to navigate between dashboards
          await navigateTo(activeDashboardId);
          currentDashboardIdRef.current = activeDashboardId;
        }
      } catch {
        isInitializingRef.current = false;

        if (!hasRetriedRef.current) {
          // Auto-retry once with a fresh SDK context and fresh embed URL
          hasRetriedRef.current = true;
          reset();
          invalidateSession();
        } else {
          // Retry also failed — surface the error
          hasRetriedRef.current = false;
          invalidateSession();
          setProviderError(new Error('Dashboard failed to load. Please try again or select a different report.'));
          setActiveDashboardId(null);
        }
      }
    };

    initialize();
  }, [
    activeDashboardId,
    sessionBaseUrl,
    isEmbedded,
    embed,
    navigateTo,
    reset,
    getEmbedUrl,
    onEmbedSuccess,
    invalidateSession,
  ]);

  return (
    <QuickSightEmbedContext.Provider
      value={{
        showDashboard,
        hideDashboard,
        activeDashboardId,
        isLoading,
        error: providerError,
        clearError,
      }}
    >
      {children}
      <Container sx={{ display: activeDashboardId ? 'block' : 'none' }}>
        <Box sx={{ position: 'relative', minHeight: 500 }}>
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                bgcolor: 'background.paper',
                zIndex: 10,
                pt: 8,
              }}
            >
              <CircularProgress />
            </Box>
          )}
          <Box
            ref={containerRef}
            data-testid="dashboard-embed-container"
            sx={{ width: '100%', '& iframe': { border: 'none' } }}
          />
        </Box>
      </Container>
    </QuickSightEmbedContext.Provider>
  );
};

export const useQuickSightEmbedContext = () => {
  const context = useContext(QuickSightEmbedContext);

  if (!context) {
    throw new Error('useQuickSightEmbedContext must be used within a QuickSightEmbedProvider');
  }

  return context;
};
